import { useCallback, useEffect, useState } from "react";
import { NavigateFunction } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import { toast } from "@/hooks/use-toast";
import { usePolling } from "@/hooks/usePolling";
import { CreateTaskDialogs } from "./CreateTaskDialogs";
import { QueueDetailTable } from "./QueueDetailTable";
import {
  getQueueDetails,
  deleteQueueItem,
  flushQueue,
  QueueDetails,
  QueueKey,
  QueueSummaryResponse,
  QUEUE_LABELS,
} from "@/services/workerApi";

// Section Files : sélection d'une file via boutons-compteurs (live), inspection
// du détail, suppression unitaire, vidage, et création manuelle de tâches.
// Intégrée à la Vue d'ensemble pour retrouver l'esprit page unique d'avant.
export function FilesSection({
  summary,
  live,
  navigate,
  onMutated,
}: {
  summary: QueueSummaryResponse | null;
  live: boolean;
  navigate: NavigateFunction;
  onMutated: () => void;
}) {
  const [detailsType, setDetailsType] = useState<QueueKey>("enrichment");
  const [details, setDetails] = useState<QueueDetails | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchDetails = useCallback(async () => {
    try {
      const d = await getQueueDetails(navigate, detailsType, { start: 0, limit: 25 });
      setDetails(d);
    } catch {
      // non bloquant : on garde la dernière vue connue
    } finally {
      setLoading(false);
    }
  }, [navigate, detailsType]);

  // Appel immédiat au montage et à chaque changement de file.
  useEffect(() => {
    setLoading(true);
    setDetails(null);
    void fetchDetails();
  }, [fetchDetails]);

  // Rafraîchissement périodique (sans double appel immédiat : géré ci-dessus).
  usePolling(fetchDetails, 5000, { enabled: live, immediate: false });

  const refreshAll = useCallback(() => {
    void fetchDetails();
    onMutated();
  }, [fetchDetails, onMutated]);

  async function onDelete(item: unknown) {
    try {
      await deleteQueueItem(navigate, { type: detailsType, item });
      refreshAll();
    } catch (e) {
      toast({ variant: "destructive", title: "Échec de la suppression", description: (e as Error)?.message });
    }
  }

  async function onFlush() {
    const ok = await confirmDialog({
      title: "Vider la file",
      description: `La file « ${QUEUE_LABELS[detailsType]} » sera entièrement vidée. Action irréversible.`,
      confirmLabel: "Vider",
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await flushQueue(navigate, detailsType);
      toast({ title: "File vidée", description: `${res.removed.toLocaleString("fr-FR")} élément(s) supprimé(s).` });
      refreshAll();
    } catch (e) {
      toast({ variant: "destructive", title: "Échec du vidage", description: (e as Error)?.message });
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Détail d'une file</CardTitle>
        </CardHeader>
        <CardContent>
          <QueueDetailTable
            details={details}
            detailsType={detailsType}
            summary={summary}
            onChangeType={setDetailsType}
            onDelete={onDelete}
            onFlush={onFlush}
            loading={loading}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Créer une tâche</CardTitle>
        </CardHeader>
        <CardContent>
          <CreateTaskDialogs navigate={navigate} onCreated={refreshAll} />
        </CardContent>
      </Card>
    </div>
  );
}
